import {
  AI_RECOMPOSE_MODE,
  AI_SUGGESTION_MODE,
  type SuggestionOrRecomposeMode,
} from 'src/data/generation/const';

import { GENERATION_TOOL, type GenerationTool } from './const';

const RECOMPOSE_TOOL_MODES = [AI_RECOMPOSE_MODE.relaxed, AI_RECOMPOSE_MODE.strict];

const TOOL_MODE_MAP: Record<string, SuggestionOrRecomposeMode[]> = {
  /**
   * "basic" tools
   */
  [GENERATION_TOOL.basicContentImprover]: [AI_RECOMPOSE_MODE.relaxed, AI_RECOMPOSE_MODE.polished],
  [GENERATION_TOOL.basicEli5]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicSentenceExpander]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicAida]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicPas]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicBab]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicPerfectHeadline]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.basicPersuasiveBulletPoint]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicFeatureToBenefitsPractical]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicValueProposition]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicProductDescriptionList]: [AI_SUGGESTION_MODE.list],
  [GENERATION_TOOL.basicProductDescriptionRelaxed]: [AI_RECOMPOSE_MODE.relaxed],
  [GENERATION_TOOL.basicBlogIdeas]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicVideoIdeas]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicTalkingPointsToParagraph]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicProductReview]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.generalProductListing]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.summarizer]: [
    AI_SUGGESTION_MODE.summarize,
    AI_SUGGESTION_MODE.tldr,
    AI_SUGGESTION_MODE.topic,
  ],
  [GENERATION_TOOL.basicPersonalBio]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicCompanyBio]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicEngagingQuestions]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicQuoraAnswers]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.generalPrompt]: [AI_SUGGESTION_MODE.default],

  /**
   * "basic DD" tools
   */
  [GENERATION_TOOL.basicFacebookAd]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.basicGoogleAd]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.basicTwitterAd]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicLinkedinTextAd]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicPinterestAd]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicLandingPage]: [AI_SUGGESTION_MODE.standard],
  [GENERATION_TOOL.basicLandingPageSeo]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.basicInstagramSocial]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.basicFacebookSocial]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.basicTwitterSocial]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.basicLinkedinSocial]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.basicYoutubeSocial]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.basicPinterestPinSocial]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.basicColdEmail]: [AI_SUGGESTION_MODE.standard],
  [GENERATION_TOOL.basicPromotionalEmail]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.basicEmailSubjectLine]: [AI_SUGGESTION_MODE.standard],
  [GENERATION_TOOL.basicSms]: [AI_SUGGESTION_MODE.sms160],

  /**
   * "product" data-driven tools
   */
  [GENERATION_TOOL.facebookPrimaryTextProduct]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.BAB,
    AI_SUGGESTION_MODE.FAB,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.facebookHeadlineProduct]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.question,
    // AI_SUGGESTION_MODE.hook, not supported by AC currently
    AI_SUGGESTION_MODE.CTA,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.googleHeadlineProduct]: [
    AI_SUGGESTION_MODE.fluent,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.googleMediumHeadlineProduct]: [
    AI_SUGGESTION_MODE.fluent,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.googleDescriptionProduct]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.googleLongHeadlineProduct]: [
    AI_SUGGESTION_MODE.fluent,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.googleShortDescriptionProduct]: [
    AI_SUGGESTION_MODE.fluent,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.googleShoppingAdTitle]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.googleShoppingAdDescription]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.googleAppAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.googleDemandGenAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.googleDisplayAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.googlePerformanceMaxAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.googleRSA]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.googleShoppingAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.googleVideoAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.facebookMixtureAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.twitterMixtureAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.pinterestMixtureAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.pinterestMixturePost]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.youtubeMixturePost]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.linkedinMixtureAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.linkedinDocumentAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.linkedinVideoAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.linkedinTextAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.linkedinMessageAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.linkedinConversationAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.linkedinLeadGenFormAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.taboolaAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.outbrainAd]: [AI_SUGGESTION_MODE.auto],
  [GENERATION_TOOL.taboolaHeadlineProduct]: [
    AI_SUGGESTION_MODE.fluent,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.taboolaDescriptionProduct]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.outbrainHeadlineProduct]: [
    AI_SUGGESTION_MODE.fluent,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.outbrainDescriptionProduct]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.twitterTitleProduct]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.benefit,
    AI_SUGGESTION_MODE.feature,
    AI_SUGGESTION_MODE.pain,
    AI_SUGGESTION_MODE.brand,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.twitterCopyProduct]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.list,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.pinterestTitleProduct]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.pinterestDescriptionProduct]: [
    AI_SUGGESTION_MODE.engaging,
    // AI_SUGGESTION_MODE.creative, was same text as engaging need to check if there is a difference and should be shown
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinHeadlineSingleImage]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.benefit,
    AI_SUGGESTION_MODE.feature,
    AI_SUGGESTION_MODE.pain,
    AI_SUGGESTION_MODE.brand,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinIntroSingleImage]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.list,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinHeadlineProduct]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.benefit,
    AI_SUGGESTION_MODE.feature,
    AI_SUGGESTION_MODE.pain,
    AI_SUGGESTION_MODE.brand,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinIntroProduct]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.list,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  // LinkedIn Message Ad individual tools
  [GENERATION_TOOL.linkedinMessageAdSubject]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.linkedinMessageAdMessage]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.list,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinMessageAdCta]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.customFormula,
  ],
  // LinkedIn Conversational Ad individual tools
  [GENERATION_TOOL.linkedinConversationalAdOpeningMessage]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.list,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinConversationalAdPrimaryCtaButtonText]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.linkedinConversationalAdFollowUpMessage]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.list,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinConversationalAdSecondaryCtaButtonText]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.customFormula,
  ],
  // LinkedIn Lead Gen Form Ad individual tools
  [GENERATION_TOOL.linkedinLeadGenFormAdPrimaryText]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.list,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinLeadGenFormAdHeadline]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.benefit,
    AI_SUGGESTION_MODE.feature,
    AI_SUGGESTION_MODE.pain,
    AI_SUGGESTION_MODE.brand,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinLeadGenFormAdOfferHeadline]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.benefit,
    AI_SUGGESTION_MODE.feature,
    AI_SUGGESTION_MODE.pain,
    AI_SUGGESTION_MODE.brand,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.linkedinLeadGenFormAdOfferDetails]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.list,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.articleBlogHeadline]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.productLaunchMultiPrompt]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.landingPageProductAnnouncementMultiPrompt]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.emailDripMultiPrompt]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.customMultiPrompt]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.imageWithDescription]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.imageByStyle]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.imageImprove]: [AI_SUGGESTION_MODE.engaging],
  [GENERATION_TOOL.imageScorer]: [AI_SUGGESTION_MODE.engaging],

  /**
   * "landing" data-driven tools
   */
  [GENERATION_TOOL.landingHeadline]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.pain,
    AI_SUGGESTION_MODE.feature,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.landingSubheader]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.FAB,
    AI_SUGGESTION_MODE.BAB,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.landingCTA]: [AI_SUGGESTION_MODE.standard],
  [GENERATION_TOOL.landingBenefits]: [AI_SUGGESTION_MODE.list, AI_SUGGESTION_MODE.customFormula],
  [GENERATION_TOOL.landingFeatures]: [AI_SUGGESTION_MODE.list, AI_SUGGESTION_MODE.customFormula],
  [GENERATION_TOOL.landingSEOMetadata]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.landingSEOMetaTitle]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.landingSectionHeadline]: [AI_SUGGESTION_MODE.creative],

  /**
   * "product listing" data-driven tools
   */
  [GENERATION_TOOL.amazonProductListing]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.walmartProductListing]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.customFormula,
  ],
  /**
   * deprecated "product listing" data-driven tools
   */
  [GENERATION_TOOL.amazonProductDescription]: [
    AI_SUGGESTION_MODE.default,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.amazonFeatureList]: [
    AI_SUGGESTION_MODE.default,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.walmartProductDescription]: [
    AI_SUGGESTION_MODE.default,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.walmartFeatureList]: [
    AI_SUGGESTION_MODE.default,
    AI_SUGGESTION_MODE.customFormula,
  ],

  /**
   * "social" data-driven tools
   */
  [GENERATION_TOOL.instagramCaption]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.PAS,
    // AI_SUGGESTION_MODE.BAB, disabled for now until we have ac support
    // AI_SUGGESTION_MODE.fourPs, disabled for now until we have ac support
    // AI_SUGGESTION_MODE.oneTwoThreeFourFormula,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.facebookPost]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.fourPs,
    AI_SUGGESTION_MODE.BAB,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.tweet]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.hookBodyCta,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.linkedinPost]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.BAB,
    AI_SUGGESTION_MODE.fourPs,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.pinterestPostDescription]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.pinterestPostTitle]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.youtubeTitle]: [AI_SUGGESTION_MODE.creative, AI_SUGGESTION_MODE.customFormula],
  [GENERATION_TOOL.youtubeDescription]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],

  /**
   * "content" data-driven tools
   */
  [GENERATION_TOOL.facebookPrimaryTextContent]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.quotes,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.facebookHeadlineContent]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.taboolaHeadlineContent]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.outbrainHeadlineContent]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.twitterCopyContent]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.pinterestTitleContent]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.pinterestDescriptionContent]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  /**
   * deprecated generation tools
   */
  [GENERATION_TOOL.linkedinHeadlineContent]: [AI_SUGGESTION_MODE.creative],
  [GENERATION_TOOL.linkedinIntroContent]: [AI_SUGGESTION_MODE.creative],

  /**
   * "email" and "sms" data-driven tools
   */
  [GENERATION_TOOL.emailSubjectLine]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.emailPromotionalSubjectLine]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.emailPromotionalBody]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.emailColdOutreachSubjectLine]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.emailColdOutreachBody]: [
    AI_SUGGESTION_MODE.standard,
    AI_SUGGESTION_MODE.customFormula,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.BAB,
    AI_SUGGESTION_MODE.guided,
  ],
  [GENERATION_TOOL.emailSubject]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.pain,
    AI_SUGGESTION_MODE.attention,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.emailBody]: [
    AI_SUGGESTION_MODE.engaging,
    AI_SUGGESTION_MODE.PAS,
    AI_SUGGESTION_MODE.AIDA,
    AI_SUGGESTION_MODE.fourPs,
    AI_SUGGESTION_MODE.FAB,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.emailMixture]: [AI_SUGGESTION_MODE.engaging, AI_SUGGESTION_MODE.customFormula],
  [GENERATION_TOOL.emailHeadline]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.emailCta]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.emailIntroParagraph]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.emailSectionHeadline]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.smsPromotional]: [
    AI_SUGGESTION_MODE.sms320,
    AI_SUGGESTION_MODE.sms160,
    AI_SUGGESTION_MODE.sms480,
  ],

  /**
   * "teads" data-driven tools
   */
  [GENERATION_TOOL.teadsSingleImageHeadline]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.teadsSingleImageDescription]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.teadsSingleVideoHeadline]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.teadsSingleVideoDescription]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.teadsCarouselHeadline]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.teadsSocialLayout1Headline]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.teadsSocialLayout1PrimaryText]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.teadsSocialLayout1Description]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],
  [GENERATION_TOOL.teadsSocialLayout2PrimaryText]: [
    AI_SUGGESTION_MODE.creative,
    AI_SUGGESTION_MODE.customFormula,
  ],

  /**
   * Freestyle tools
   */
  [GENERATION_TOOL.freestyle]: [AI_SUGGESTION_MODE.default, AI_SUGGESTION_MODE.customFormula],
  [GENERATION_TOOL.createYourOwn]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.chromeExtensionExtract]: [AI_SUGGESTION_MODE.default],

  /**
   * LPCO tools
   */
  [GENERATION_TOOL.lpcoLandingHeadline]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.lpcoLandingCTA]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.lpcoLandingProductDescription]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.lpcoLandingProductName]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.lpcoLandingCopy]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.wtmDefault]: [AI_SUGGESTION_MODE.default],

  /**
   * Blog wizard tools
   */
  [GENERATION_TOOL.blogDescription]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.blogPostSectionHeadline]: [AI_RECOMPOSE_MODE.relaxed],
  [GENERATION_TOOL.blogPostSectionSubHeadline]: [AI_RECOMPOSE_MODE.relaxed],
  [GENERATION_TOOL.blogPostIntroParagraph]: [AI_RECOMPOSE_MODE.relaxed],

  /**
   * Long form tools
   */
  [GENERATION_TOOL.longFormGeneral]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.longFormBlank]: [AI_SUGGESTION_MODE.default],
  [GENERATION_TOOL.longFormContentImprover]: [AI_SUGGESTION_MODE.default],

  /**
   * Rewrite tools
   */
  [GENERATION_TOOL.facebookHeadlineProductRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.facebookPrimaryTextProductRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.googleHeadlineProductRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.googleDescriptionProductRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.outbrainHeadlineProductRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.taboolaHeadlineProductRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.twitterCopyProductRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.pinterestDescriptionProductRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.landingHeadlineRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.landingSubheaderRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.landingSEOMetadataRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.emailBodyRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.emailSubjectLineRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.emailColdOutreachBodyRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.emailPromotionalBodyRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.smsPromotionalRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.linkedinHeadlineSingleImageRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.linkedinIntroSingleImageRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.instagramCaptionRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.facebookPostRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.pinterestPinTitleRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.pinterestPinDescriptionRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.tweetRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.linkedinPostRewrite]: RECOMPOSE_TOOL_MODES,
  [GENERATION_TOOL.emailSubjectRewrite]: RECOMPOSE_TOOL_MODES,
};

export const getToolGenerationModes = (
  generationTool: GenerationTool
): SuggestionOrRecomposeMode[] => TOOL_MODE_MAP[generationTool] || [];

export const getToolDefaultGenerationMode = (
  generationTool: GenerationTool
): SuggestionOrRecomposeMode => {
  const modes = getToolGenerationModes(generationTool);
  return modes[0];
};
